import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Switch, Button, Space, Typography, Divider, Tag, Tooltip, message, Collapse, Modal, Spin } from 'antd';
import {
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  FileTextOutlined,
  FunctionOutlined,
  CodeOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import CustomDimensionModal from '../../components/CustomDimensionModal';
import MetricConfigModal from '../../components/MetricConfigModal';
import {
  getDefaultConfig,
  createNewCustomDimension,
  createNewCustomMetric
} from '../../data/defaultDimensionConfig';
import { scenarioMetricApi } from '../../services/api';

const { Title, Text } = Typography;
const { Panel } = Collapse;

/**
 * 指标配置页面
 * UI 风格：按维度组织，使用 Collapse + Panel 折叠面板
 * 数据：从后端加载/保存
 */
const MetricConfig = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams();

  // 加载/保存状态
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 维度数据
  const [presetDimensions, setPresetDimensions] = useState([]);
  const [customDimensions, setCustomDimensions] = useState([]);

  // 自定义维度弹窗
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDimension, setEditingDimension] = useState(null);

  // 自定义指标弹窗
  const [metricModalVisible, setMetricModalVisible] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [currentDimensionKey, setCurrentDimensionKey] = useState(null);

  // 初始化：从后端加载配置
  useEffect(() => {
    if (scenarioId) {
      loadConfig();
    } else {
      initDefault();
    }
  }, [scenarioId]);

  // 使用默认配置初始化
  const initDefault = () => {
    const defaultConfig = getDefaultConfig();
    // 确保预设维度的所有指标默认启用
    const presetDimensionsWithAllEnabled = defaultConfig.presetDimensions.map(dim => ({
      ...dim,
      presetMetrics: dim.presetMetrics.map(metric => ({
        ...metric,
        enabled: true  // 预设维度下的所有指标默认启用
      }))
    }));
    setPresetDimensions(presetDimensionsWithAllEnabled);
    setCustomDimensions(defaultConfig.customDimensions);
  };

  // 从后端加载配置
  const loadConfig = async () => {
    setLoading(true);
    try {
      const config = await scenarioMetricApi.getConfig(scenarioId);
      if (config && (config.presetDimensions?.length > 0 || config.customDimensions?.length > 0)) {
        setPresetDimensions(config.presetDimensions || []);
        setCustomDimensions(config.customDimensions || []);
      } else {
        initDefault();
      }
    } catch (error) {
      console.error('加载指标配置失败:', error);
      initDefault();
    } finally {
      setLoading(false);
    }
  };

  // 切换预设维度状态（级联切换所有预设指标）
  const handlePresetDimensionToggle = (key, enabled) => {
    setPresetDimensions(prev =>
      prev.map(dimension =>
        dimension.key === key
          ? {
              ...dimension,
              enabled,
              presetMetrics: dimension.presetMetrics.map(metric => ({
                ...metric,
                enabled: enabled
              }))
            }
          : dimension
      )
    );
    message.success(`${enabled ? '启用' : '禁用'}预设维度成功，预设指标已联动${enabled ? '启用' : '禁用'}`);
  };

  // 切换自定义维度状态（级联切换所有指标）
  const handleCustomDimensionToggle = (key, enabled) => {
    setCustomDimensions(prev =>
      prev.map(dim =>
        dim.key === key
          ? {
              ...dim,
              enabled,
              presetMetrics: dim.presetMetrics.map(metric => ({
                ...metric,
                enabled: enabled
              })),
              customMetrics: dim.customMetrics.map(metric => ({
                ...metric,
                enabled: enabled
              }))
            }
          : dim
      )
    );
    message.success(`${enabled ? '启用' : '禁用'}自定义维度成功，所有指标已联动${enabled ? '启用' : '禁用'}`);
  };

  // 切换预设维度中的预设指标状态
  const handlePresetDimensionMetricToggle = (dimensionKey, metricKey, enabled) => {
    let successMessage = '';

    setPresetDimensions(prev => {
      return prev.map(dimension => {
        if (dimension.key === dimensionKey) {
          const updatedPresetMetrics = dimension.presetMetrics.map(metric =>
            metric.key === metricKey ? { ...metric, enabled } : metric
          );

          const hasEnabledPresetMetrics = updatedPresetMetrics.some(metric => metric.enabled);
          const hasEnabledCustomMetrics = dimension.customMetrics && dimension.customMetrics.some(metric => metric.enabled);
          const shouldEnableDimension = enabled ? true : (hasEnabledPresetMetrics || hasEnabledCustomMetrics);

          if (enabled) {
            successMessage = '启用预设指标成功，维度已自动启用';
          } else {
            if (!hasEnabledPresetMetrics && !hasEnabledCustomMetrics) {
              successMessage = '禁用预设指标成功，维度已自动禁用';
            } else {
              successMessage = '禁用预设指标成功';
            }
          }

          return {
            ...dimension,
            enabled: shouldEnableDimension,
            presetMetrics: updatedPresetMetrics
          };
        }
        return dimension;
      });
    });

    setTimeout(() => message.success(successMessage), 0);
  };

  // 切换自定义维度中的预设指标状态
  const handleCustomDimensionPresetMetricToggle = (dimensionKey, metricKey, enabled) => {
    let successMessage = '';

    setCustomDimensions(prev => {
      return prev.map(dimension => {
        if (dimension.key === dimensionKey) {
          const updatedPresetMetrics = dimension.presetMetrics.map(metric =>
            metric.key === metricKey ? { ...metric, enabled } : metric
          );

          const hasEnabledPresetMetrics = updatedPresetMetrics.some(metric => metric.enabled);
          const hasEnabledCustomMetrics = dimension.customMetrics && dimension.customMetrics.some(metric => metric.enabled);
          const shouldEnableDimension = enabled ? true : (hasEnabledPresetMetrics || hasEnabledCustomMetrics);

          if (enabled) {
            successMessage = '启用预设指标成功，维度已自动启用';
          } else {
            if (!hasEnabledPresetMetrics && !hasEnabledCustomMetrics) {
              successMessage = '禁用预设指标成功，维度已自动禁用';
            } else {
              successMessage = '禁用预设指标成功';
            }
          }

          return {
            ...dimension,
            enabled: shouldEnableDimension,
            presetMetrics: updatedPresetMetrics
          };
        }
        return dimension;
      });
    });

    setTimeout(() => message.success(successMessage), 0);
  };

  // 切换自定义维度中的自定义指标状态
  const handleCustomDimensionCustomMetricToggle = (dimensionKey, metricKey, enabled) => {
    let successMessage = '';

    setCustomDimensions(prev => {
      return prev.map(dimension => {
        if (dimension.key === dimensionKey) {
          const updatedCustomMetrics = dimension.customMetrics.map(metric =>
            metric.key === metricKey ? { ...metric, enabled } : metric
          );

          const hasEnabledPresetMetrics = dimension.presetMetrics && dimension.presetMetrics.some(metric => metric.enabled);
          const hasEnabledCustomMetrics = updatedCustomMetrics.some(metric => metric.enabled);
          const shouldEnableDimension = enabled ? true : (hasEnabledPresetMetrics || hasEnabledCustomMetrics);

          if (enabled) {
            successMessage = '启用自定义指标成功，维度已自动启用';
          } else {
            if (!hasEnabledPresetMetrics && !hasEnabledCustomMetrics) {
              successMessage = '禁用自定义指标成功，维度已自动禁用';
            } else {
              successMessage = '禁用自定义指标成功';
            }
          }

          return {
            ...dimension,
            enabled: shouldEnableDimension,
            customMetrics: updatedCustomMetrics
          };
        }
        return dimension;
      });
    });

    setTimeout(() => message.success(successMessage), 0);
  };

  // 添加自定义维度
  const handleAddDimension = () => {
    setEditingDimension(null);
    setCurrentDimensionKey(null);
    setModalVisible(true);
  };

  // 编辑自定义维度
  const handleEditDimension = (dimension) => {
    setEditingDimension(dimension);
    setCurrentDimensionKey(dimension.key);
    setModalVisible(true);
  };

  // 删除自定义维度
  const handleDeleteDimension = (key) => {
    const dimension = customDimensions.find(dim => dim.key === key);
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除自定义维度"${dimension?.name}"吗？删除后将无法恢复。`,
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        setCustomDimensions(prev => prev.filter(dim => dim.key !== key));
        message.success('删除自定义维度成功');
      }
    });
  };

  // 保存维度配置
  const handleSaveDimension = (values) => {
    if (editingDimension) {
      setCustomDimensions(prev =>
        prev.map(dim =>
          dim.key === editingDimension.key
            ? {
                ...dim,
                ...values,
                updatedAt: new Date().toISOString().split('T')[0],
                presetMetrics: dim.presetMetrics,
                customMetrics: dim.customMetrics
              }
            : dim
        )
      );
      message.success('更新自定义维度成功');
    } else {
      const newDimension = createNewCustomDimension(values);
      setCustomDimensions(prev => [...prev, newDimension]);
      message.success('添加自定义维度成功');
    }
    setModalVisible(false);
  };

  // 为维度添加自定义指标
  const handleAddCustomMetricForDimension = (dimensionKey) => {
    setCurrentDimensionKey(dimensionKey);
    setEditingMetric(null);
    setMetricModalVisible(true);
  };

  // 编辑维度中的自定义指标
  const handleEditCustomMetricForDimension = (dimensionKey, metric) => {
    setCurrentDimensionKey(dimensionKey);
    setEditingMetric(metric);
    setMetricModalVisible(true);
  };

  // 删除维度中的自定义指标
  const handleDeleteCustomMetricForDimension = (dimensionKey, metricKey) => {
    const dimension = customDimensions.find(dim => dim.key === dimensionKey);
    const metric = dimension?.customMetrics.find(m => m.key === metricKey);

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除自定义指标"${metric?.name}"吗？删除后将无法恢复。`,
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        setCustomDimensions(prev =>
          prev.map(dim =>
            dim.key === dimensionKey
              ? {
                  ...dim,
                  customMetrics: dim.customMetrics.filter(m => m.key !== metricKey)
                }
              : dim
          )
        );
        message.success('删除自定义指标成功');
      }
    });
  };

  // 保存指标配置
  const handleSaveMetric = (values) => {
    if (editingMetric) {
      setCustomDimensions(prev =>
        prev.map(dim =>
          dim.key === currentDimensionKey
            ? {
                ...dim,
                customMetrics: dim.customMetrics.map(metric =>
                  metric.key === editingMetric.key
                    ? { ...metric, ...values, updatedAt: new Date().toISOString().split('T')[0] }
                    : metric
                )
              }
            : dim
        )
      );
      message.success('更新自定义指标成功');
    } else {
      const newMetric = createNewCustomMetric(values);
      setCustomDimensions(prev =>
        prev.map(dim =>
          dim.key === currentDimensionKey
            ? {
                ...dim,
                customMetrics: [...dim.customMetrics, newMetric]
              }
            : dim
        )
      );
      message.success('添加自定义指标成功');
    }
    setMetricModalVisible(false);
    setCurrentDimensionKey(null);
  };

  // 保存配置到后端
  const handleSave = async () => {
    const hasEnabledPresetDimension = presetDimensions.some(dimension => dimension.enabled);
    const hasEnabledCustomDimension = customDimensions.some(dimension => dimension.enabled);

    if (!hasEnabledPresetDimension && !hasEnabledCustomDimension) {
      message.warning('至少要有一个维度配置启用');
      return;
    }

    if (!scenarioId) {
      message.error('场景ID不能为空');
      return;
    }

    setSaving(true);
    try {
      await scenarioMetricApi.saveConfig(scenarioId, {
        presetDimensions,
        customDimensions
      });
      message.success('配置保存成功');
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败: ' + (error.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  const getBaseOnText = (baseOn) => {
    if (!baseOn || baseOn.includes('none')) return '独立维度';

    const baseMap = {
      'document': '单据维度',
      'rule_avg': '规则平均维度',
      'rule_detail': '规则明细维度'
    };

    if (Array.isArray(baseOn)) {
      return baseOn.map(b => baseMap[b] || b).join(', ');
    }

    return baseMap[baseOn] || baseOn;
  };

  const getDimensionIcon = (key) => {
    const iconMap = {
      'document': <FileTextOutlined />,
      'rule_avg': <BarChartOutlined />,
      'rule_detail': <DatabaseOutlined />
    };
    return iconMap[key] || <DatabaseOutlined />;
  };

  // 渲染预设指标列表（3列网格）
  const renderPresetMetrics = (metrics, dimensionKey, isCustomDimension = false) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {metrics.map((metric) => (
        <div
          key={metric.key}
          className={`metric-item p-3 border rounded-lg ${metric.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <CheckCircleOutlined className={`text-sm ${metric.enabled ? 'text-green-500' : 'text-gray-400'}`} />
              <Text className={`text-sm font-medium ${metric.enabled ? 'text-green-700' : 'text-gray-500'}`}>
                {metric.name}
              </Text>
            </div>
            <Switch
              size="small"
              checked={metric.enabled}
              onChange={(checked) => {
                if (isCustomDimension) {
                  handleCustomDimensionPresetMetricToggle(dimensionKey, metric.key, checked);
                } else {
                  handlePresetDimensionMetricToggle(dimensionKey, metric.key, checked);
                }
              }}
            />
          </div>
          <Text className="text-xs text-gray-500 block">
            {metric.description}
          </Text>
        </div>
      ))}
    </div>
  );

  // 渲染自定义指标列表
  const renderCustomMetrics = (metrics, dimensionKey) => (
    <div className="space-y-3">
      {metrics.map((metric) => (
        <div
          key={metric.key}
          className={`metric-item p-3 border rounded-lg ${metric.enabled ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <CodeOutlined className={`text-sm ${metric.enabled ? 'text-orange-500' : 'text-gray-400'}`} />
              <Text className={`text-sm font-medium ${metric.enabled ? 'text-orange-700' : 'text-gray-500'}`}>
                {metric.name}
              </Text>
              <Tag color="orange" size="small">自定义</Tag>
            </div>
            <Space>
              <Switch
                size="small"
                checked={metric.enabled}
                onChange={(checked) => handleCustomDimensionCustomMetricToggle(dimensionKey, metric.key, checked)}
              />
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditCustomMetricForDimension(dimensionKey, metric)}
                className="text-blue-500 hover:text-blue-600"
              />
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteCustomMetricForDimension(dimensionKey, metric.key)}
                className="text-red-500 hover:text-red-600"
              />
            </Space>
          </div>
          <Text className="text-xs text-gray-500 block mb-2">
            {metric.description}
          </Text>
          {metric.code && (
            <div className="flex items-center space-x-2">
              <Text className="text-xs text-gray-400">代码:</Text>
              <Text className="text-xs font-mono text-gray-600">{metric.code}</Text>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Spin spinning={loading}>
      <div style={{ minHeight: '100%', background: '#f5f5f5', display: 'flex', flexDirection: 'column', paddingLeft: 16, paddingRight: 16 }}>
        {/* 页面头部 */}
        <div style={{
          background: '#fff',
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            >
              返回
            </Button>
            <div style={{ height: '24px', width: '1px', background: '#e8e8e8' }} />
            <div>
              <span style={{ fontSize: '18px', fontWeight: 600, color: '#262626' }}>
                <SettingOutlined style={{ marginRight: 8 }} />
                指标配置管理
              </span>
              <Text type="secondary" style={{ marginLeft: 12, fontSize: '13px' }}>
                按维度配置评测指标，系统预设维度不允许添加自定义指标
              </Text>
            </div>
          </div>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            保存配置
          </Button>
        </div>

        <div style={{ padding: '16px 0' }} className="dimension-config-content">
          {/* 预设维度配置 */}
          <Card
            title={
              <Space>
                <DatabaseOutlined />
                <span>预设维度</span>
                <Tooltip title="系统内置的3个标准统计维度">
                  <InfoCircleOutlined className="text-gray-400" />
                </Tooltip>
              </Space>
            }
            className="mb-6 dimension-card"
          >
            <Collapse ghost>
              {presetDimensions.map((dimension) => (
                <Panel
                  key={dimension.key}
                  header={
                    <div className="flex items-center justify-between w-full pr-4">
                      <Space>
                        <Switch
                          size="small"
                          checked={dimension.enabled}
                          onChange={(checked) => handlePresetDimensionToggle(dimension.key, checked)}
                          onClick={(checked, e) => {
                            if (e && e.stopPropagation) {
                              e.stopPropagation();
                            }
                          }}
                        />
                        <span className="text-blue-500">{getDimensionIcon(dimension.key)}</span>
                        <Text strong className={dimension.enabled ? 'text-green-600' : 'text-gray-400'}>
                          {dimension.name}
                        </Text>
                        <Tag color={dimension.enabled ? 'green' : 'default'} size="small">
                          预设维度
                        </Tag>
                      </Space>
                      <Text type="secondary" className="text-sm">
                        {dimension.description}
                      </Text>
                    </div>
                  }
                >
                  <div className="pl-6">
                    {/* 预设指标 */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <Title level={5} className="mb-0">预设指标</Title>
                        <Text className="text-xs text-gray-500">
                          共 {dimension.presetMetrics.filter(m => m.enabled).length} / {dimension.presetMetrics.length} 个启用
                        </Text>
                      </div>
                      {renderPresetMetrics(dimension.presetMetrics, dimension.key)}
                    </div>
                  </div>
                </Panel>
              ))}
            </Collapse>
          </Card>

          {/* 自定义维度配置 */}
          <Card
            title={
              <Space>
                <SettingOutlined />
                <span>自定义维度</span>
                <Tooltip title="用户自定义的业务维度">
                  <InfoCircleOutlined className="text-gray-400" />
                </Tooltip>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddDimension}
              >
                添加自定义维度
              </Button>
            }
            className="mb-6 dimension-card"
          >
            {customDimensions.length > 0 ? (
              <Collapse ghost>
                {customDimensions.map((dimension) => (
                  <Panel
                    key={dimension.key}
                    header={
                      <div className="flex items-center justify-between w-full pr-4">
                        <Space>
                          <Switch
                            size="small"
                            checked={dimension.enabled}
                            onChange={(checked) => handleCustomDimensionToggle(dimension.key, checked)}
                            onClick={(checked, e) => {
                              if (e && e.stopPropagation) {
                                e.stopPropagation();
                              }
                            }}
                          />
                          <SettingOutlined className="text-green-500" />
                          <Text strong className={dimension.enabled ? 'text-green-600' : 'text-gray-400'}>
                            {dimension.name}
                          </Text>
                          <Tag color="green" size="small">自定义</Tag>
                          <Tag color="blue" size="small">{getBaseOnText(dimension.baseOn)}</Tag>
                        </Space>
                        <Space>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditDimension(dimension);
                            }}
                            className="text-blue-500 hover:text-blue-600"
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDimension(dimension.key);
                            }}
                            className="text-red-500 hover:text-red-600"
                          />
                        </Space>
                      </div>
                    }
                  >
                    <div className="pl-6">
                      {/* 预设指标 */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                          <Title level={5} className="mb-0">预设指标</Title>
                          <Text className="text-xs text-gray-500">
                            共 {dimension.presetMetrics.filter(m => m.enabled).length} / {dimension.presetMetrics.length} 个启用
                          </Text>
                        </div>
                        {renderPresetMetrics(dimension.presetMetrics, dimension.key, true)}
                      </div>

                      {/* 自定义指标 */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <Title level={5} className="mb-0">自定义指标</Title>
                          {dimension.baseOn && dimension.baseOn.includes('none') ? (
                            <Button
                              size="small"
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={() => handleAddCustomMetricForDimension(dimension.key)}
                              className="text-xs"
                            >
                              添加自定义指标
                            </Button>
                          ) : (
                            <Tooltip title="只有当基于维度选择'无'时，才允许添加自定义指标。基于预设维度的自定义维度会继承预设维度的指标体系。">
                              <Button
                                size="small"
                                type="dashed"
                                icon={<PlusOutlined />}
                                disabled
                                className="text-xs"
                              >
                                添加自定义指标
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                        {dimension.customMetrics.length > 0 ? (
                          renderCustomMetrics(dimension.customMetrics, dimension.key)
                        ) : (
                          <div className="text-center py-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                            <CodeOutlined className="text-2xl text-gray-300 mb-2" />
                            {dimension.baseOn && dimension.baseOn.includes('none') ? (
                              <Text type="secondary" className="block text-sm">
                                暂无自定义指标，点击上方按钮添加
                              </Text>
                            ) : (
                              <div>
                                <Text type="secondary" className="block text-sm mb-2">
                                  基于预设维度的自定义维度不支持添加自定义指标
                                </Text>
                                <Text type="secondary" className="block text-xs text-gray-400">
                                  如需添加自定义指标，请将"基于维度"设置为"无"
                                </Text>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Panel>
                ))}
              </Collapse>
            ) : (
              <div className="text-center py-8">
                <SettingOutlined className="text-4xl text-gray-300 mb-4" />
                <Text type="secondary" className="block mb-4">
                  暂无自定义维度，点击上方按钮添加
                </Text>
              </div>
            )}
          </Card>

          {/* 配置说明 */}
          <Card title="配置说明" className="mt-6">
          <div className="space-y-4">
            <div>
              <Title level={5}>预设维度</Title>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li><strong>单据维度</strong>：以整张业务单据为单位进行指标统计</li>
                <li><strong>规则平均维度</strong>：以（单据×规则）实例为单位，对所有规则的指标取平均值</li>
                <li><strong>规则明细维度</strong>：以单据内具体审核规则为单位进行指标统计</li>
              </ul>
            </div>

            <Divider />

            <div>
              <Title level={5}>预设指标</Title>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li><strong>无风险精确率</strong>：识别为无风险的样本中，实际无风险的比例</li>
                <li><strong>无风险召回率</strong>：实际无风险的样本中，被正确识别的比例</li>
                <li><strong>有风险精确率</strong>：识别为有风险的样本中，实际有风险的比例</li>
                <li><strong>有风险召回率</strong>：实际有风险的样本中，被正确识别的比例</li>
                <li><strong>准确率</strong>：所有预测正确的样本占总样本的比例</li>
              </ul>
            </div>

            <Divider />

            <div>
              <Title level={5}>自定义维度</Title>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li><strong>维度名称</strong>：自定义维度的显示名称</li>
                <li><strong>维度代码</strong>：用于脚本中引用的唯一标识</li>
                <li><strong>基于维度</strong>：选择基于哪个预设维度进行二次分组，或选择"无"创建独立维度</li>
                <li><strong>预设指标</strong>：每个维度都包含5个标准预设指标，可独立启用/禁用</li>
                <li><strong>自定义指标</strong>：可为每个维度添加特定的评测指标</li>
              </ul>
            </div>
          </div>
        </Card>
        </div>

        {/* 自定义维度弹窗 */}
        <CustomDimensionModal
          visible={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setCurrentDimensionKey(null);
          }}
          onSave={handleSaveDimension}
          editingDimension={editingDimension}
          presetDimensions={presetDimensions}
        />

        {/* 自定义指标弹窗 */}
        <MetricConfigModal
          visible={metricModalVisible}
          onCancel={() => {
            setMetricModalVisible(false);
            setCurrentDimensionKey(null);
          }}
          onSave={handleSaveMetric}
          editingMetric={editingMetric}
          presetDimensions={presetDimensions}
          customDimensions={customDimensions}
        />
      </div>
    </Spin>
  );
};

export default MetricConfig;
